import React, { useState, useContext } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { excavatorService } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { useTheme } from "@mui/material/styles";

const ExcavatorAdd = () => {
  const navigate = useNavigate();
  const { isManager } = useContext(AuthContext);
  const theme = useTheme(); // Material-UI theme

  const [formData, setFormData] = useState({
    model: "",
    manufacturer: "",
    serialNumber: "",
    year: new Date().getFullYear(),
    status: "operational",
    size: "",
    machineType: "",
    location: "",
    hours: 0,
    nextMaintenanceHours: 100,
    description: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isManager()) {
      setError("You don't have permission to add excavators.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Format data for submission
      const excavatorData = {
        ...formData,
        year: parseInt(formData.year),
        hours: parseInt(formData.hours),
        nextMaintenanceHours: parseInt(formData.nextMaintenanceHours),
        size: formData.size ? parseFloat(formData.size) : undefined,
      };

      await excavatorService.create(excavatorData);
      setSuccess(true);

      // Redirect after a short delay
      setTimeout(() => {
        navigate("/excavators");
      }, 2000);
    } catch (err) {
      console.error("Error adding excavator:", err);
      setError(
        err.response?.data?.message ||
        "Failed to add excavator. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <div
        className="d-flex justify-content-between align-items-center mb-4"
        style={{
          borderBottom: `2px solid ${theme.palette.primary.main}`,
          paddingBottom: '1rem'
        }}
      >
        <h2 style={{ color: theme.palette.text.primary }}>Add New Excavator</h2>
        <Button
          variant="outline-secondary"
          onClick={() => navigate(-1)}
          style={{
            borderColor: theme.palette.secondary.main,
            color: theme.palette.secondary.main,
          }}
        >
          Back
        </Button>
      </div>

      <Card
        style={{
          backgroundColor: theme.palette.background.paper,
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
        }}
      >
        <Card.Body style={{ backgroundColor: theme.palette.background.paper }}>
          {error && (
            <Alert
              variant="danger"
              style={{
                backgroundColor: theme.palette.error.light || '#ffebee',
                borderColor: theme.palette.error.main,
                color: theme.palette.error.dark,
              }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              variant="success"
              style={{
                backgroundColor: theme.palette.success.light || '#e8f5e8',
                borderColor: theme.palette.success.main,
                color: theme.palette.success.dark,
              }}
            >
              Excavator added successfully! Redirecting...
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Manufacturer*
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Caterpillar"
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Model*
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    required
                    placeholder="e.g., CAT 320"
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Serial Number*
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="serialNumber"
                    value={formData.serialNumber}
                    onChange={handleChange}
                    required
                    placeholder="e.g., CAT-320-12345"
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Year*
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    required
                    min="1900"
                    max={new Date().getFullYear()}
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Status
                  </Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    <option value="operational">Operational</option>
                    <option value="in maintenance">Under Maintenance</option>
                    <option value="in repair">In Repair</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Size (tons)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    placeholder="e.g., 20.5"
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Machine Type
                  </Form.Label>
                  <Form.Select
                    name="machineType"
                    value={formData.machineType}
                    onChange={handleChange}
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    <option value="">Select Type</option>
                    <option value="Crawler">Crawler</option>
                    <option value="Wheeled">Wheeled</option>
                    <option value="Frontloader">Frontloader</option>
                    <option value="Backhoe">Backhoe</option>
                    <option value="Skid-steer">Skid-steer</option>
                    <option value="Bulldozer">Bulldozer</option>
                    <option value="Grader">Grader</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Current Hours
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="hours"
                    value={formData.hours}
                    onChange={handleChange}
                    min="0"
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Location
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="e.g., North Site"
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme.palette.text.primary }}>
                    Next Maintenance Hours
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="nextMaintenanceHours"
                    value={formData.nextMaintenanceHours}
                    onChange={handleChange}
                    min={formData.hours}
                    style={{
                      backgroundColor: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: theme.palette.text.primary }}>
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Enter excavator description..."
                style={{
                  backgroundColor: theme.palette.background.default,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                }}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ color: theme.palette.text.primary }}>
                Notes
              </Form.Label>
              <Form.Control
                as="textarea"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Enter additional notes..."
                style={{
                  backgroundColor: theme.palette.background.default,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                }}
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={() => navigate("/excavators")}
                disabled={loading}
                style={{
                  borderColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.main,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || success}
                style={{
                  backgroundColor: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                }}
              >
                {loading ? (
                  <>
                    <Spinner
                      size="sm"
                      animation="border"
                      className="me-1"
                      style={{ color: theme.palette.primary.contrastText }}
                    />
                    Adding...
                  </>
                ) : (
                  "Add Excavator"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ExcavatorAdd;
